import {
  createSortedRowModel,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type RowData,
} from "@tanstack/react-table"
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ListFilterIcon,
  type LucideIcon,
} from "lucide-react"
import {
  Fragment,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from "react"
import { Link, useNavigate, useSearchParams } from "react-router"
import { Button } from "@/primitives/button.tsx"
import { Skeleton } from "@/primitives/skeleton.tsx"
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/primitives/table.tsx"
import { cn } from "@/primitives/utils"
import {
  filterRows,
  orderChosen,
  ordersOf,
  pinnedFirst,
  stackOf,
  turnedPageScroll,
  type OrderChoice,
  type Stack,
} from "./data-table.ts"
import { countOf, EmptyState, tableListingOf, type TableListing } from "./empty-state.tsx"
import { FilterMenu, SearchField, type Facet } from "./filter-bar.tsx"
import { Key } from "./key.tsx"
import { listRowFrame } from "./list-row.tsx"
import { Updating, useLadder, useStickyTop } from "./page-chrome.tsx"
import {
  Count,
  DeskDisplay,
  PageToolbar,
  type OrderBy,
  type ToolbarDisplay,
} from "./page-toolbar.tsx"
import { readTableView, writeTableView, type Sort, type TableView } from "./table-view.ts"
import { useRowNavigation } from "./use-row-navigation.ts"
import { useViewport } from "./viewport.tsx"

/**
 * One value a row can be filtered by: `value` goes in the URL, `label` is what a Member reads, and
 * `glyph` and `secondary` stand beside it in the Filter menu, as an Avatar and a job title do.
 * `keywords` find it in the menu's search without showing, as a Member's email does.
 */
export type FacetValue = {
  value: string
  label: string
  glyph?: ReactNode
  secondary?: string
  keywords?: string
}

/**
 * One column. `cell` renders it. `sortBy` makes it sortable, by the string or number it gives each
 * row — a number sorts as a number, so $10 comes after $9 — and `firstDirection` is the way its
 * first click sorts: `desc` for activity and cost, where the most is what a Member looks for.
 * `facet` makes it filterable, by the values it gives each row: a row passes when any of them is
 * chosen, and a row passes the table's filters when it passes every column's. The choices on offer
 * are the values the rows have, so a filter can never be offered that matches nothing; `facetIcon`
 * marks it in the Filter menu and its chip, and `facetLabel` names a chosen value no row has, as
 * one from an old link is.
 *
 * The choices about looks are closed, and there is no styling passthrough: `width` (the table's
 * columns are fixed, so they never jump as rows filter or page; `fill` takes what's left),
 * `align: "end"` for a numeric column, head and cells alike, and `emphasis`, where `key` renders
 * the cell's text as a Key.
 *
 * `stack` is where the cell goes when the rows stack, on `compact` (`Stack`): exactly one column is
 * the row's `title`. `cell` is told which `form` it draws for, so a cell can say inline on a stacked
 * row what the table keeps in a Toggletip, or leave out a glyph the row's `lead` stands in for. The
 * title's `lead` (a Member's Disc, a Project's tile) stands before both of a stacked row's lines,
 * a picture and not a control, so a tap on it opens the row; and its `detail` (a job title, a
 * description), prose of any length, ends line two, where the cut takes it before a shorter fact.
 * `orderLabel` names a sortable column
 * in Order by where its header says less, as "Name" for the Projects table's "Project".
 */
export type DataTableColumn<Row extends RowData, Id extends string> = {
  id: Id
  header: string
  stack: Stack
  lead?: (row: Row) => ReactNode
  detail?: (row: Row) => ReactNode
  orderLabel?: string
  sortBy?: (row: Row) => string | number
  firstDirection?: "asc" | "desc"
  facet?: (row: Row) => readonly FacetValue[]
  facetIcon?: LucideIcon
  facetLabel?: (value: string) => string
  width?: keyof typeof widths
  align?: "end"
} & (
  | { cell: (row: Row, form: CellForm) => ReactNode; emphasis?: "strong" | "muted" }
  | { cell: (row: Row, form: CellForm) => string; emphasis: "key" }
)

/** What a cell draws for: a table's row on the desk, or a stacked row on `compact`. */
export type CellForm = "table" | "stack"

const widths = { fill: "", xs: "w-20", sm: "w-28", md: "w-40", lg: "w-56" } as const

const emphases = { strong: "font-medium text-foreground", muted: "text-muted-foreground" } as const

/** Where the table sits: under the page's header, full bleed; or inside a card with others. */
const chromes = {
  page: { edge: "first:pl-6 last:pr-6", bar: "px-6 compact:px-4" },
  card: { edge: "first:pl-4 last:pr-4", bar: "px-4" },
} as const

const pageSize = 50

/** Skeleton cell widths, fixed so the rows read as varied text yet never move between renders. */
const skeletonWidths = ["w-3/5", "w-2/5", "w-1/2", "w-3/4", "w-1/3", "w-2/3", "w-1/2", "w-2/5"]

const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
})

// A click on one of these inside a row is theirs, not the row's.
const ownClicks =
  "a, button, input, label, [role=menuitem], [role=checkbox], [data-slot=tooltip-trigger]"

/*
 * The table every list in the web app is: a toolbar holding the Filter menu, its chips and a
 * search, with the count and the page's Display options at its end; sortable headers that stick
 * under the page's chrome; rows that open what they list; fifty rows to a page; and an empty state
 * that tells "nothing here yet" from "nothing matches these filters".
 *
 * What a Member is looking at — filters, search, sort and page — lives in the URL's search
 * parameters, so a reload keeps it and a link reproduces it for a colleague. The table owns its own
 * parameters and leaves the page's others alone, and its part of the URL is only as long as the
 * view's departures from the default.
 *
 * `chrome` is where it sits. `page` is a whole page's list: its toolbar is the PageToolbar, and the
 * table runs full bleed with its head stuck under it. `card` sits in a card among others, its
 * filters in a compact row of its own. `display` is the view's options, as data, behind Display on
 * the desk and in the View sheet on `compact`.
 *
 * On `compact` the rows stack: a list of two-line rows, at least 56px, each column in the place its
 * `stack` gives it and the row's ⋯ always showing; in a card, the last row leaves its rule to the
 * card's edge, unless the pager follows it. The View sheet, or a card's Display, then holds
 * an Order by of its sortable columns (`ordersOf`), even one, as its column heads aren't there to
 * sort by or reverse;
 * `deskOrderBy` leads the desk's Display menu with the same, as Projects and People do. Choosing an
 * order writes the sort a click on its column's head would, and its Direction, there on `compact`
 * alone, reverses it as a second click would, so the URL, and the view a link opens at a desk, are
 * the same from either.
 *
 * Turning the page brings the table back to its top, so the new page is read from its first row:
 * a page's table to its rows, just under the toolbar, and a card's to the card's top, just under
 * the page's chrome. Focus held on the pager stays there, on the button pressed or, at the first or
 * last page, the other.
 *
 * `rows` is undefined while they load, and the table shows skeleton rows at its real geometry.
 * `pending` says a refetch is keeping the old rows on screen: they dim, and a spinner turns.
 * `dimmed` fades the rows that are there to be read rather than worked in, as an archived Project
 * is; `emptyActions` go under the empty state, such as the page's own create button. `pinned` picks
 * rows that lead the table whatever it is sorted by, as a Member's Personal Project leads the
 * Projects page. `rowHref` makes each row open a page, by a click anywhere on it or, from the
 * keyboard, j/k and Enter; `actions` gives each row a last column of hover-revealed RowActions;
 * `search` makes the rows searchable by the text it gives each, or `matches` by a rule of the
 * table's own, such as every word typed found somewhere in a person.
 */
export function DataTable<Row extends RowData, Id extends string>({
  of,
  chrome,
  rows,
  rowKey,
  columns,
  sort,
  rowHref,
  search,
  matches,
  actions,
  display,
  pending = false,
  dimmed,
  pinned,
  emptyActions,
  deskOrderBy = false,
}: {
  of: TableListing
  chrome: keyof typeof chromes
  rows: readonly Row[] | undefined
  rowKey: (row: Row) => string
  columns: readonly DataTableColumn<Row, Id>[]
  sort: Sort<Id>
  rowHref?: (row: Row) => string
  search?: (row: Row) => string
  matches?: (row: Row, query: string) => boolean
  actions?: (row: Row) => ReactNode
  display?: ToolbarDisplay
  pending?: boolean
  dimmed?: (row: Row) => boolean
  pinned?: (row: Row) => boolean
  emptyActions?: ReactNode
  deskOrderBy?: boolean
}) {
  const listing = tableListingOf(of)
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const stickyTop = useStickyTop()
  const { compact } = useViewport()
  const body = useRef<HTMLTableSectionElement>(null)
  const list = useRef<HTMLUListElement>(null)
  const root = useRef<HTMLDivElement>(null)
  const pager = useRef<HTMLElement>(null)
  // Set as the pager turns the page, saying whether it held focus, so the page that comes in is
  // brought to its top once it has rendered.
  const turning = useRef<{ focused: boolean } | null>(null)
  const { stuck } = useLadder()
  const { edge, bar } = chromes[chrome]
  const stacked = stackOf(columns)

  const shape = useMemo(
    () => ({
      ...(listing.prefix && { prefix: listing.prefix }),
      facets: columns.filter((column) => column.facet).map((column) => column.id),
      sortable: columns.filter((column) => column.sortBy).map((column) => column.id),
      sort,
      searchable: Boolean(search || matches),
    }),
    [columns, sort, listing.prefix, search, matches],
  )
  const view = readTableView(params, shape)
  const showView = (next: TableView<Id, Id>) =>
    setParams((page) => writeTableView(next, shape, page), { replace: true })

  const [draft, setDraft] = useSearchDraft(view.q ?? "", (q) => showView({ ...view, q, page: 1 }))
  const { matching, filtered, counts } = filterRows({
    rows: rows ?? [],
    facets: new Map(columns.flatMap(({ id, facet }) => (facet ? [[id, facet] as const] : []))),
    search,
    matches,
    filters: view.filters,
    query: draft,
  })

  const definitions = useMemo(
    () =>
      columns.map(({ id, sortBy }): ColumnDef<typeof features, Row> => ({
        id,
        accessorFn: (row) => sortBy?.(row) ?? "",
        enableSorting: Boolean(sortBy),
        sortFn: (a, b, columnId) => {
          const [x, y] = [a.getValue(columnId), b.getValue(columnId)]
          return typeof x === "number" && typeof y === "number"
            ? x - y
            : String(x).localeCompare(String(y))
        },
      })),
    [columns],
  )

  const table = useTable({
    features,
    columns: definitions,
    data: matching,
    getRowId: rowKey,
    state: { sorting: [{ id: view.sort.column, desc: view.sort.direction === "descending" }] },
  })

  // Paged here rather than by the table library, so a page past the last — from an old link, or
  // after rows went away — shows the last page instead of an empty one.
  const sorted = pinned
    ? pinnedFirst(table.getRowModel().rows, (row) => pinned(row.original))
    : table.getRowModel().rows
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize))
  const page = Math.min(view.page, pageCount)
  const shown = sorted.slice((page - 1) * pageSize, page * pageSize)

  useRowNavigation(compact ? list : body, { enabled: Boolean(rowHref) && shown.length > 0 })

  // Before the new page paints, so it never shows at the old page's scroll.
  useLayoutEffect(() => {
    const turn = turning.current
    turning.current = null
    if (!turn) return
    const rowsShown = compact ? list.current : (body.current?.closest("table") ?? null)
    const top = chrome === "page" ? rowsShown : root.current
    if (top) {
      const scroller = scrollerOf(top)
      const scrollTop = turnedPageScroll({
        scrollTop: scroller.scrollTop,
        top: top.getBoundingClientRect().top - visibleTopOf(scroller),
        covered: stuck,
      })
      if (scrollTop !== undefined) scroller.scrollTop = scrollTop
    }
    // Focus stays on the button pressed, so the keyboard can page again at once rather than tab
    // through the rows; at the first or last page, where it is disabled (and may still hold the
    // focus until the browser lets it go), the other takes it.
    const held = document.activeElement
    const kept =
      held instanceof HTMLButtonElement && !held.disabled && pager.current?.contains(held)
    if (turn.focused && !kept)
      pager.current
        ?.querySelector<HTMLElement>("button:not(:disabled)")
        ?.focus({ preventScroll: true })
  }, [page, compact, chrome, stuck])

  const clearFilters = () =>
    showView({ ...readTableView(new URLSearchParams(), shape), sort: view.sort })

  const facets: Facet[] = columns.flatMap(({ id, header, facetIcon, facetLabel }) => {
    const values = counts.get(id)
    if (!values) return []
    return [
      {
        id,
        label: header,
        icon: facetIcon ?? ListFilterIcon,
        mode: "multi",
        values: values
          .toSorted((a, b) => a.value.label.localeCompare(b.value.label))
          .map(({ value: { value, label, glyph, secondary, keywords }, count }) => ({
            id: value,
            label,
            glyph,
            secondary,
            keywords,
            count,
          })),
        chosen: view.filters[id],
        onChange: (chosen) =>
          showView({ ...view, filters: { ...view.filters, [id]: chosen }, page: 1 }),
        ...(facetLabel && { labelOf: facetLabel }),
      },
    ]
  })

  // The order, offered where the column heads can't be clicked and there is a choice to make, or
  // wherever the page asks for it on the desk; a dot on View or Display says a sort other than the
  // table's own is in force.
  const orders = ordersOf(columns, sort.column)
  // On `compact` even one order is offered, as its Direction is how a phone reverses it.
  const ordered = orders.length > 0 && (compact || deskOrderBy)
  const onOrderChoice = (choice: OrderChoice) =>
    showView({ ...view, sort: orderChosen(orders, view.sort, choice), page: 1 })
  const orderBy: OrderBy | undefined = ordered
    ? {
        value: view.sort.column,
        options: orders,
        onChange: (column) => onOrderChoice({ column }),
        direction: {
          value: view.sort.direction,
          onChange: (direction) => onOrderChoice({ direction }),
        },
      }
    : undefined
  const resorted = view.sort.column !== sort.column || view.sort.direction !== sort.direction
  const viewOptions: ToolbarDisplay | undefined = orderBy
    ? { rows: display?.rows ?? [], nonDefault: Boolean(display?.nonDefault) || resorted }
    : display

  const searched = search || matches
  const count = rows && countOf(matching.length, of)
  const toolbar =
    chrome === "page" ? (
      <PageToolbar
        pending={pending}
        facets={facets}
        onClearFilters={clearFilters}
        {...(searched && {
          search: { placeholder: `Search ${listing.many}`, value: draft, onChange: setDraft },
        })}
        {...(count !== undefined && { count })}
        {...(orderBy && { orderBy })}
        {...(viewOptions && { display: viewOptions })}
      />
    ) : (
      (facets.some((facet) => facet.values.length >= 2 || facet.chosen.length > 0) ||
        searched ||
        viewOptions) && (
        // On `compact` the row wraps: the search takes the full width on a line of its own, under
        // the Filter, the count and Display.
        <div
          className={cn(
            "flex h-9 items-center gap-2 border-b border-border-subtle compact:h-auto compact:min-h-9 compact:flex-wrap compact:py-1",
            bar,
          )}
        >
          <FilterMenu facets={facets} onClear={clearFilters} />
          {searched && (
            <div className="contents compact:order-last compact:block compact:basis-full compact:pb-1 compact:*:w-full">
              <SearchField
                placeholder={`Search ${listing.many}`}
                value={draft}
                onChange={setDraft}
              />
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {pending && <Updating />}
            {count !== undefined && <Count count={count} />}
            <DeskDisplay orderBy={orderBy} display={viewOptions} />
          </div>
        </div>
      )
    )

  if (rows && rows.length === 0)
    return (
      <div>
        {chrome === "page" && toolbar}
        <EmptyState of={of} framed={chrome === "card"} actions={emptyActions} />
      </div>
    )

  const openRow = (event: MouseEvent<HTMLElement>, href: string) => {
    if (!(event.target instanceof Element) || event.target.closest(ownClicks)) return
    if (window.getSelection()?.toString()) return
    if (event.metaKey || event.ctrlKey) window.open(href, "_blank", "noopener")
    else void navigate(href)
  }

  const sortOn = (column: DataTableColumn<Row, Id>) => {
    const active = view.sort.column === column.id
    const first = column.firstDirection === "desc" ? "descending" : "ascending"
    const direction = active
      ? view.sort.direction === "ascending"
        ? "descending"
        : "ascending"
      : first
    showView({ ...view, sort: { column: column.id, direction }, page: 1 })
  }

  const cellClass = (column: { align?: "end" }) =>
    cn(
      "overflow-hidden border-b border-border-subtle text-ellipsis",
      edge,
      column.align === "end" && "text-right",
    )

  return (
    <div ref={root}>
      {toolbar}
      {rows && matching.length === 0 ? (
        <EmptyState
          of={`${of}-filtered`}
          framed={chrome === "card"}
          actions={
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <>
          {compact ? (
            <ul
              ref={list}
              className={cn(
                "transition-opacity duration-150",
                pending && "opacity-70",
                // A card's own edge ends its last row, unless the pager follows it.
                chrome === "card" && pageCount <= 1 && "*:last:border-b-0",
              )}
            >
              {rows
                ? shown.map((row) => (
                    <StackedRow
                      key={row.id}
                      row={row.original}
                      stacked={stacked}
                      href={rowHref?.(row.original)}
                      onOpen={openRow}
                      actions={actions}
                      dimmed={dimmed?.(row.original) ?? false}
                    />
                  ))
                : skeletonWidths.map((width, index) => (
                    <li
                      key={index}
                      className="flex min-h-14 flex-col justify-center gap-2 border-b border-border-subtle px-4"
                    >
                      <Skeleton className={cn("h-3.5", width)} />
                      <Skeleton className="h-2.5 w-28" />
                    </li>
                  ))}
            </ul>
          ) : (
            <table
              data-slot="table"
              className="w-full table-fixed border-separate border-spacing-0 text-ui"
            >
              {/* On the page the head sticks, on the panel's own fill so rows pass under it; in a
                card it stays put, on the card's fill. */}
              <TableHeader
                className={cn(chrome === "page" ? ["sticky z-10", stickyTop] : "bg-card")}
              >
                <tr>
                  {columns.map((column) => (
                    <TableHead
                      key={column.id}
                      data-align={column.align}
                      aria-sort={
                        view.sort.column === column.id && column.sortBy
                          ? view.sort.direction
                          : undefined
                      }
                      className={cn(
                        "group/head border-b border-border-subtle",
                        edge,
                        widths[column.width ?? "fill"],
                      )}
                    >
                      {column.sortBy ? (
                        <SortButton
                          header={column.header}
                          end={column.align === "end"}
                          direction={
                            view.sort.column === column.id ? view.sort.direction : undefined
                          }
                          next={column.firstDirection === "desc" ? "descending" : "ascending"}
                          onClick={() => sortOn(column)}
                        />
                      ) : (
                        column.header
                      )}
                    </TableHead>
                  ))}
                  {actions && (
                    <TableHead className={cn("w-12 border-b border-border-subtle", edge)}>
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  )}
                </tr>
              </TableHeader>
              <TableBody
                ref={body}
                className={cn("transition-opacity duration-150", pending && "opacity-70")}
              >
                {rows
                  ? shown.map((row) => {
                      const href = rowHref?.(row.original)
                      return (
                        <TableRow
                          key={row.id}
                          {...(href
                            ? {
                                "data-row": "",
                                onClick: (event: MouseEvent<HTMLElement>) => openRow(event, href),
                              }
                            : {})}
                          className={cn(
                            "scroll-mt-32 border-b-0",
                            dimmed?.(row.original) && "opacity-70",
                            href &&
                              "cursor-pointer data-highlighted:bg-accent has-[[data-row-link]:focus-visible]:bg-accent",
                          )}
                        >
                          {columns.map((column, index) => (
                            <TableCell
                              key={column.id}
                              data-align={column.align}
                              className={cn(
                                cellClass(column),
                                column.emphasis !== "key" &&
                                  column.emphasis &&
                                  emphases[column.emphasis],
                                href &&
                                  index === 0 &&
                                  "relative before:absolute before:inset-y-0 before:left-0 before:w-0.5 group-has-[[data-row-link]:focus-visible]/row:before:bg-ring group-data-highlighted/row:before:bg-ring",
                              )}
                            >
                              {href && index === 0 ? (
                                <Link
                                  to={href}
                                  data-row-link
                                  className="outline-none focus-visible:outline-none"
                                >
                                  <CellContent column={column} row={row.original} />
                                </Link>
                              ) : (
                                <CellContent column={column} row={row.original} />
                              )}
                            </TableCell>
                          ))}
                          {actions && (
                            <TableCell
                              className={cn(cellClass({ align: "end" }), "overflow-visible")}
                            >
                              {actions(row.original)}
                            </TableCell>
                          )}
                        </TableRow>
                      )
                    })
                  : skeletonWidths.map((width, index) => (
                      <TableRow key={index} className="border-b-0 hover:bg-transparent">
                        {columns.map((column) => (
                          <TableCell key={column.id} className={cellClass(column)}>
                            <Skeleton
                              className={cn("h-3", column.align === "end" ? "ml-auto w-10" : width)}
                            />
                          </TableCell>
                        ))}
                        {actions && <TableCell className={cellClass({ align: "end" })} />}
                      </TableRow>
                    ))}
              </TableBody>
            </table>
          )}
          {pageCount > 1 && (
            <Pager
              page={page}
              pageCount={pageCount}
              from={(page - 1) * pageSize + 1}
              to={(page - 1) * pageSize + shown.length}
              of={sorted.length}
              bar={bar}
              ref={pager}
              onPage={(next) => {
                turning.current = {
                  focused: pager.current?.contains(document.activeElement) ?? false,
                }
                showView({ ...view, page: next })
              }}
            />
          )}
        </>
      )}
    </div>
  )
}

/** The nearest box around `element` that scrolls it vertically, or else the document's. */
function scrollerOf(element: Element) {
  for (let at = element.parentElement; at; at = at.parentElement) {
    const { overflowY } = getComputedStyle(at)
    if ((overflowY === "auto" || overflowY === "scroll") && at.scrollHeight > at.clientHeight)
      return at
  }
  return document.scrollingElement ?? document.documentElement
}

/** Where a scroller's visible top stands in the viewport: the document's is the viewport's. */
const visibleTopOf = (scroller: Element) =>
  scroller === document.scrollingElement ? 0 : scroller.getBoundingClientRect().top

/** How long typing in the search pauses before the URL takes what was typed. */
const searchSettles = 200

/**
 * The search as typed, ahead of the URL. The rows filter on every keystroke, but the URL is
 * written once typing settles: a URL write lands a render later, and a field bound to it straight
 * would lose a keystroke typed in between. A change to the URL from elsewhere (Clear, the back
 * button) replaces what was typed.
 */
function useSearchDraft(q: string, commit: (q: string) => void) {
  const [draft, setDraft] = useState(q)
  const settle = useEffectEvent(commit)
  useEffect(() => setDraft(q), [q])
  useEffect(() => {
    if (draft === q) return
    const timer = setTimeout(() => settle(draft), searchSettles)
    return () => clearTimeout(timer)
  }, [draft, q])
  return [draft, setDraft] as const
}

function CellContent<Row extends RowData, Id extends string>({
  column,
  row,
}: {
  column: DataTableColumn<Row, Id>
  row: Row
}) {
  return column.emphasis === "key" ? (
    <Key value={column.cell(row, "table")} />
  ) : (
    column.cell(row, "table")
  )
}

/** A cell as a stacked row draws it: a Key as a Key, and nothing for an empty one. */
function stackedCell<Row extends RowData, Id extends string>(
  column: DataTableColumn<Row, Id>,
  row: Row,
) {
  if (column.emphasis === "key") {
    const value = column.cell(row, "stack")
    return value ? <Key value={value} /> : null
  }
  const content = column.cell(row, "stack")
  return content === "" || content === null || content === undefined || content === false
    ? null
    : content
}

/**
 * One row as it stacks on `compact`, at least 56px: the title's `lead`, then line one, the title
 * (text wrapping to two lines at most) and the trailing cells that have something to say, beside
 * the title's first line, over line two, one line in `text-xs` cut short at its end: the subtitle,
 * meta and labelled cells that have something to say, then the title's `detail`, a dot between
 * each. The row's ⋯ ends it, always showing. Like a table's row, the whole row opens what it lists,
 * its title the link the keyboard opens.
 */
function StackedRow<Row extends RowData, Id extends string>({
  row,
  stacked: { title, trailing, second },
  href,
  onOpen,
  actions,
  dimmed,
}: {
  row: Row
  stacked: ReturnType<typeof stackOf<DataTableColumn<Row, Id>>>
  href: string | undefined
  onOpen: (event: MouseEvent<HTMLElement>, href: string) => void
  actions: ((row: Row) => ReactNode) | undefined
  dimmed: boolean
}) {
  const named = stackedCell(title, row)
  const detail = title.detail?.(row)
  // The detail is prose of any length, so it comes last, where the cut takes it before a fact.
  const facts = [
    ...second.flatMap((column) => {
      const content = stackedCell(column, row)
      if (content === null) return []
      return [
        { id: column.id, content, label: column.stack === "labelled" ? column.header : undefined },
      ]
    }),
    ...(detail ? [{ id: "detail", content: detail, label: undefined }] : []),
  ]
  const ends = trailing.flatMap((column) => {
    const content = stackedCell(column, row)
    return content === null ? [] : [{ id: column.id, content }]
  })
  return (
    <li
      {...(href && {
        "data-row": "",
        onClick: (event: MouseEvent<HTMLElement>) => onOpen(event, href),
      })}
      // `data-revealed` shows the row's ⋯ without a hover, as a stacked row always does.
      data-revealed
      className={cn(
        listRowFrame,
        "group/row relative transition-colors duration-100 ease-snappy",
        dimmed && "opacity-70",
        href &&
          "cursor-pointer before:absolute before:inset-y-0 before:left-0 before:w-0.5 hover:bg-accent has-aria-expanded:bg-accent data-highlighted:bg-accent data-highlighted:before:bg-ring has-[[data-row-link]:focus-visible]:bg-accent has-[[data-row-link]:focus-visible]:before:bg-ring",
      )}
    >
      {/* The lead decorates the title beside it, so a screen reader skips it. */}
      {title.lead && (
        <span aria-hidden className="flex shrink-0">
          {title.lead(row)}
        </span>
      )}
      <div className="min-w-0 flex-1">
        {/* Line one keeps to its first line: a title that wraps runs on below, while what ends the
            line sits in a box one line of the title tall, beside that first line. */}
        <div className="flex items-start gap-2">
          {/* Text wraps to a second line before it is cut; a title that draws its own line (a
              Member's name and badges) keeps to it, held to the row's width so it cuts its own
              text rather than running under what ends the line. */}
          <div
            className={cn(
              "min-w-0 flex-1 text-ui font-medium text-foreground",
              typeof named === "string" ? "line-clamp-2 wrap-break-word" : "flex items-center",
            )}
          >
            {href ? (
              <Link
                to={href}
                data-row-link
                className={cn(
                  "outline-none focus-visible:outline-none",
                  typeof named !== "string" && "flex min-w-0",
                )}
              >
                {named}
              </Link>
            ) : (
              named
            )}
          </div>
          {ends.map(({ id, content }) => (
            <span
              key={id}
              className="flex h-lh shrink-0 items-center text-ui text-muted-foreground tabular-nums"
            >
              {content}
            </span>
          ))}
        </div>
        {/* One run of text on one line, cut only at its end. A fact of plain text is cut short
            there; one with a header or drawn as more than text is a box of its own, which the
            ellipsis stands in for whole, so a header never shows without its value. A box opening
            the line is held to its width. */}
        {facts.length > 0 && (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {facts.map(({ id, content, label }, index) => {
              const dot = index > 0 && (
                <span aria-hidden className="mx-1.5 text-subtle-foreground">
                  ·
                </span>
              )
              const text = typeof content === "string" || typeof content === "number"
              return text && !label ? (
                <Fragment key={id}>
                  {dot}
                  {content}
                </Fragment>
              ) : (
                <span key={id} className="inline-flex max-w-full items-center">
                  {dot}
                  {label && <span className="mr-1 shrink-0 text-subtle-foreground">{label}</span>}
                  <span className="min-w-0 truncate">{content}</span>
                </span>
              )
            })}
          </div>
        )}
      </div>
      {actions && <span className="flex shrink-0">{actions(row)}</span>}
    </li>
  )
}

/**
 * A sortable column's heading: the header, and an arrow for the way it sorts — shown while it is
 * the sort, and faintly on hover otherwise, pointing the way a click would sort it.
 */
function SortButton({
  header,
  end,
  direction,
  next,
  onClick,
}: {
  header: string
  end: boolean
  direction: Sort<string>["direction"] | undefined
  next: Sort<string>["direction"]
  onClick: () => void
}) {
  const Icon = (direction ?? next) === "descending" ? ArrowDownIcon : ArrowUpIcon
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "-mx-1 inline-flex h-6 items-center gap-1 rounded-sm px-1 transition-colors duration-100 ease-snappy outline-none touch-target hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
        end && "ml-auto flex-row-reverse",
        direction && "text-foreground",
      )}
    >
      {header}
      <Icon
        aria-hidden
        className={cn(
          "size-3.5 transition-opacity duration-100",
          direction ? "opacity-100" : "reveal-on-hover-head",
        )}
      />
    </button>
  )
}

/** Where the Member is among the pages, "1–50 of 68", and the way to the others. */
function Pager({
  page,
  pageCount,
  from,
  to,
  of,
  bar,
  ref,
  onPage,
}: {
  page: number
  pageCount: number
  from: number
  to: number
  of: number
  bar: string
  ref: Ref<HTMLElement>
  onPage: (page: number) => void
}) {
  return (
    <nav ref={ref} aria-label="Pages" className={cn("flex h-10 items-center justify-between", bar)}>
      <span className="text-xs text-muted-foreground tabular-nums">
        {from}–{to} of {of}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Previous page"
          disabled={page === 1}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeftIcon />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Next page"
          disabled={page === pageCount}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRightIcon />
        </Button>
      </div>
    </nav>
  )
}
